import React, { useState, useEffect, useRef } from 'react';
import { Typography, Image } from 'lib/components';
import selectImageHandler from 'lib/util/selectImageHandler';
import { useAppDispatch, useSelectCurrentUser, actions, useSelectRequest, RequestManager } from 'store';
import { useComprehensiveTopNav, useWindowResize, useAuthenticationStorage, useCustomToast } from 'hooks';
import routes from 'lib/api/routes';
import classes from './index.module.scss';

/**
 * ProfileScreen component
 * Allows users to view and edit their profile information
 * Includes functionality for updating personal details, changing password, and uploading a profile image
 *
 * @returns {JSX.Element} - Rendered component
 */
const ProfileScreen = () => {
	// Custom toast notifications for user feedback
	const toast = useCustomToast();
	// Authentication storage for persisting user data
	const [auth, setAuth] = useAuthenticationStorage();
	// Responsive design helpers
	const { isSmallDevice, isSmallerDevice } = useWindowResize();

	// Redux state and actions
	const dispatch = useAppDispatch();
	// Active API request tracking
	const { active } = useSelectRequest();
	// Current user information from Redux store
	const user = useSelectCurrentUser();

	// Form state variables
	/** User's name input value */
	const [name, setName] = useState(user.name);
	/** User's email input value */
	const [email, setEmail] = useState(user.email);
	/** User's phone number input value */
	const [phoneNumber, setPhoneNumber] = useState(user.phoneNumber ?? '');
	/** New password input value */
	const [newPassword, setNewPassword] = useState('');
	/** Password confirmation input value */
	const [newPasswordConfirmation, setNewPasswordConfirmation] = useState('');
	/** Profile image source - defaults to placeholder avatar */
	const [profileImageString, setProfileImageString] = useState<any>('plAvatar');
	/** Reference to hidden file input for profile image upload */
	const adminProfileImageRef = useRef<HTMLInputElement>(null);

	/**
	 * Monitor API requests for user data operations
	 * Handles user fetch, update, and avatar upload requests
	 * Updates authentication storage and shows toast notifications
	 */
	useEffect(() => {
		const RM = new RequestManager(active);
		if (RM.idle()) return;

		// Handle user data fetch request
		if (RM.req(routes.USER, 'GET')) {
			if (RM.succeeded()) {
				// Update auth storage with latest user data
				setAuth({ ...auth!, user });
			}
		}

		// Handle user data update request
		if (RM.req(routes.USER, 'PUT')) {
			if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				// Update auth storage with latest user data
				setAuth({ ...auth!, user });
			}
		}

		// Handle avatar upload request
		if (RM.req(routes.UPLOAD_ID(user.id), 'POST')) {
			if (RM.pending()) {
				toast('Uploading avatar...', 'info');
			} else if (RM.failed()) {
				toast(active.message, 'error');
			} else if (RM.succeeded()) {
				toast(active.message);
				// Update auth storage with latest user data
				setAuth({ ...auth!, user });
			}
		}
	}, [active]);

	/**
	 * Handle profile update form submission
	 * Dispatches updateCurrentUser action with form data
	 */
	const handleUpdate = () => {
		dispatch(
			actions.updateCurrentUser({
				name,
				email,
				phoneNumber,
				newPassword,
				newPasswordConfirmation,
			})
		);
	};

	/**
	 * Handle profile image file selection
	 * Updates local image preview and dispatches avatar update action
	 */
	const handleFileSelect = selectImageHandler((file, fileURL) => {
		// Update local image preview
		setProfileImageString(fileURL);
		// Dispatch action to upload new avatar
		dispatch(actions.updateCurrentUserAvatar(file));
	});

	// Set up the top navigation bar with user name and edit profile title
	useComprehensiveTopNav(`${user.name} - Edit Profile`);

	return (
		<div
			className={classes['profile-container']}
			style={{
				flexDirection: 'column',
			}}
		>
			<>
				{/* Profile header section with avatar and name */}
				<div className={classes['top-container']}>
					{/* Avatar image area with upload functionality */}
					<div className={classes['image-area']}>
						<div
							className={classes['profile-image-container']}
							style={{
								margin: '2rem',
								border: 'none',
							}}
						>
							{/* Small circle overlay for image upload button */}
							<div className={classes['small-circle']}>
								<div
									onClick={() => adminProfileImageRef.current?.click()}
									className={classes['inner-circle']}
									style={{ borderColor: '#0CB9C3' }}
								>
									{/* Upload icon */}
									<Image
										src={'icImageUpload'}
										style={{ width: 12, height: 12, objectFit: 'contain' }}
									/>
									{/* Hidden file input for image upload */}
									<input
										style={{ display: 'none' }}
										onChange={handleFileSelect}
										ref={adminProfileImageRef}
										type="file"
										id="adminProfileImage"
										name="adminProfileImage"
										accept="image/*"
									/>
								</div>
							</div>
							{/* Profile image - uses user avatar or placeholder */}
							<Image src={user.avatar ?? profileImageString} style={{ width: '100%', height: '100%' }} />
						</div>
					</div>
					{/* Basic profile details */}
					<div className={classes['profile-details']}>
						{/* Name input field */}
						<div style={{ margin: '0rem 1rem 1rem 0' }}>
							<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
								Name:
							</Typography>
							<input
								className={classes['input']}
								style={{
									width: '15rem',
									minWidth: 250,
								}}
								value={name}
								onChange={(e) => setName(e.target.value)}
							/>
						</div>
					</div>
				</div>
				{/* Contact information section */}
				<div
					className={classes['bottom-container']}
					style={{
						padding: isSmallDevice ? '1rem' : undefined,
					}}
				>
					<div
						style={{
							display: 'flex',
							flexDirection: 'column',
						}}
					>
						<Typography weight="bold">Basic info:</Typography>
						{/* Contact information row */}
						<div className={classes['row']}>
							{/* Phone number input */}
							<div style={{ margin: '1rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Mobile number:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
									}}
									value={phoneNumber}
									onChange={(e) => setPhoneNumber(e.target.value)}
								/>
							</div>
							{/* Email input */}
							<div style={{ margin: '1rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Email:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
										minWidth: 180,
									}}
									value={email}
									onChange={(e) => setEmail(e.target.value)}
								/>
							</div>
						</div>
						{/* Password change row */}
						<div className={classes['row']} style={{ marginTop: '1.5rem' }}>
							{/* New password input */}
							<div style={{ margin: '0rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Password:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
										minWidth: 200,
									}}
									placeholder="***********"
									type="password"
									value={newPassword}
									onChange={(e) => setNewPassword(e.target.value)}
								/>
							</div>
							{/* Password confirmation input */}
							<div style={{ margin: '0rem 1rem 1rem 0' }}>
								<Typography color="darkgrey" variant={isSmallerDevice ? 'h5' : 'subheading2'}>
									Confirm Password:
								</Typography>
								<input
									className={`${classes['input']} ${classes['palewhite']}`}
									style={{
										width: '15rem',
										minWidth: 200,
									}}
									placeholder="***********"
									type="password"
									value={newPasswordConfirmation}
									onChange={(e) => setNewPasswordConfirmation(e.target.value)}
								/>
							</div>
						</div>
					</div>
				</div>
				{/* Form action buttons */}
				<div
					className={classes['row']}
					style={{
						margin: 'auto 0 1rem 1rem',
					}}
				>
					{/* Update profile button */}
					<button
						className={classes['button']}
						style={{
							width: 'auto',
							marginRight: '1rem',
						}}
						onClick={handleUpdate}
					>
						<p>Update Profile</p>
					</button>
					{/* Cancel button - currently disabled */}
					<button
						className={`${classes['button']} ${classes['palewhite']}`}
						style={{
							width: 'auto',
						}}
						// onClick={() => setEditView(false)}
					>
						<p>Cancel</p>
					</button>
				</div>
			</>
		</div>
	);
};

export default ProfileScreen;
